/**
 * Typography Design Tokens
 *
 * Standard typography hierarchy for consistent text styling throughout the application.
 * Defines font families, sizes, weights, and line heights.
 *
 * Usage:
 * - sx={{ fontSize: tokens.typography.fontSize.lg }}
 * - theme.tokens.typography.fontWeight.semibold
 */

export const typography = {
  /** Font family tokens */
  fontFamily: {
    /** Primary font for body text and UI elements */
    primary: '"DM Sans", Arial, sans-serif',

    /** Display font for brand headings (h1) */
    display: 'Comfortaa, Arial, sans-serif',

    /** Monospace font for code and technical content */
    mono: '"Roboto Mono", "Courier New", monospace',
  },

  /** Font size scale */
  fontSize: {
    /** 12px - Extra small text (captions, helper text) */
    xs: '12px',

    /** 14px - Small text (secondary text, labels) */
    sm: '14px',

    /** 16px - Base/medium text (body text, default) */
    md: '16px',

    /** 18px - Large text (prominent body text) */
    lg: '18px',

    /** 18px - Extra large (h3 - Heading 3) */
    xl: '20px',

    /** 20px - 2X large (h2 - Heading 2) */
    '2xl': '22px',

    /** 24px - 3X large (h1 - Heading 1) */
    '3xl': '28px',

    /** 32px - 4X large (Display text) */
    '4xl': '36px',
  },

  /** Font weight scale */
  fontWeight: {
    /** 200 - Extra light weight */
    extralight: 200,

    /** 300 - Light weight */
    light: 300,

    /** 400 - Regular/Normal weight (default) */
    regular: 400,

    /** 500 - Medium weight */
    medium: 500,

    /** 600 - Semi-bold weight */
    semibold: 600,

    /** 700 - Bold weight */
    bold: 700,
  },

  /** Line height scale */
  lineHeight: {
    /** 1.0 - None (same as font size) */
    none: 1.0,

    /** 1.25 - Tight line height (for headings) */
    tight: 1.25,

    /** 1.375 - Snug line height */
    snug: 1.375,

    /** 1.5 - Normal line height (default for body text) */
    normal: 1.5,

    /** 1.625 - Relaxed line height */
    relaxed: 1.625,

    /** 1.75 - Loose line height (for better readability) */
    loose: 1.75,

    /** 2.0 - Extra loose line height */
    extraLoose: 2.0,
  },

  /** Letter spacing scale */
  letterSpacing: {
    /** -0.05em - Tighter letter spacing */
    tighter: '-0.05em',

    /** -0.025em - Tight letter spacing */
    tight: '-0.025em',

    /** 0 - Normal letter spacing (default) */
    normal: '0',

    /** 0.025em - Wide letter spacing */
    wide: '0.025em',

    /** 0.05em - Wider letter spacing */
    wider: '0.05em',

    /** 0.1em - Widest letter spacing (for uppercase headings) */
    widest: '0.1em',
  },
} as const;

/**
 * Semantic typography tokens for specific use cases
 * These provide preset combinations for common text patterns
 */
export const semanticTypography = {
  /** Display text for hero sections (Comfortaa Light 32px) */
  display: {
    fontFamily: typography.fontFamily.display,
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.light,
    lineHeight: typography.lineHeight.tight,
  },

  /** Heading 1 - Main page heading (DM Sans ExtraLight 24px) */
  heading1: {
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.extralight,
    lineHeight: typography.lineHeight.tight,
  },

  /** Heading 2 - Section heading (DM Sans Light 20px) */
  heading2: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.light,
    lineHeight: typography.lineHeight.tight,
  },

  /** Heading 3 - Subsection heading (DM Sans Medium 18px) */
  heading3: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.snug,
  },

  /** Standard body text */
  body: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.regular,
    lineHeight: typography.lineHeight.normal,
  },

  /** Small body text */
  bodySmall: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.regular,
    lineHeight: typography.lineHeight.normal,
  },

  /** Caption text (helper text, metadata) */
  caption: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.regular,
    lineHeight: typography.lineHeight.normal,
  },

  /** Button text */
  button: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    lineHeight: typography.lineHeight.none,
    letterSpacing: typography.letterSpacing.wide,
  },

  /** Input/form field text */
  input: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.regular,
    lineHeight: typography.lineHeight.normal,
  },

  /** Info text (DM Sans Medium 14px) */
  info: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
  },

  /** Label text (DM Sans Light 14px) */
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.light,
    lineHeight: typography.lineHeight.normal,
  },

  /** Code/monospace text */
  code: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.mono,
    lineHeight: typography.lineHeight.normal,
  },
} as const;

export type FontFamilyKey = keyof typeof typography.fontFamily;
export type FontSizeKey = keyof typeof typography.fontSize;
export type FontWeightKey = keyof typeof typography.fontWeight;
export type LineHeightKey = keyof typeof typography.lineHeight;
export type LetterSpacingKey = keyof typeof typography.letterSpacing;
export type SemanticTypographyKey = keyof typeof semanticTypography;
